/**
 * CL-337A: Portal invite intent shared by AdminClients, AdminProjectDetail, and ClientPortal signup.
 */

import { collection, getDocs, limit, query, where } from "firebase/firestore";

function safeStr(value) {
  return (value ?? "").toString().trim();
}

export function normalizeInviteEmail(value) {
  return safeStr(value).toLowerCase();
}

/** CRM / portal user row is active (not archived). */
export function isArchivedPortalUser(userRow) {
  return safeStr(userRow?.status || "active").toLowerCase() === "archived";
}

/** Global Mark Beattie admin (`users.role`). */
export function isGlobalAdminPortalUser(userRow) {
  return safeStr(userRow?.role || "").toLowerCase() === "admin";
}

/**
 * Temporary policy (CL-338C): Project Administrator on a project is treated as admin-equivalent
 * (`users.role === "admin"`) until project-scoped admin permissions are built.
 */
export function isProjectAdminEquivalentRole(roleValue) {
  return safeStr(roleValue).toLowerCase() === "project_admin";
}

/**
 * Firestore `users` marker: admin access was granted via project_admin (CL-338E).
 * True global admins have `role === "admin"` without this flag.
 */
export const PROJECT_ADMIN_EQUIVALENT_FIELD = "projectAdminEquivalent";

export function isProjectAdminEquivalentUser(userRow) {
  return userRow?.[PROJECT_ADMIN_EQUIVALENT_FIELD] === true;
}

/** Global admin not promoted from project_admin collaborator save/signup. */
export function isTrueGlobalAdminUser(userRow) {
  return isGlobalAdminPortalUser(userRow) && !isProjectAdminEquivalentUser(userRow);
}

const COLLABORATOR_PERSONA_KEYS = new Set([
  "client",
  "designer",
  "engineer",
  "supplier",
  "contractor",
  "consultant",
  "reviewer",
  "trade",
  "subtrade",
  "collaborator",
  "project_admin",
]);

/**
 * Staff-only global admin (hide from CRM / link picklist). CL-338G
 * Stale `role: admin` with `primaryUserType: designer` (etc.) is NOT dedicated global admin.
 */
export function isDedicatedGlobalAdminUser(userRow) {
  if (safeStr(userRow?.role).toLowerCase() !== "admin") return false;
  if (isProjectAdminEquivalentUser(userRow)) return false;
  const primary = safeStr(userRow?.primaryUserType).toLowerCase();
  if (primary && primary !== "admin") return false;
  const construction = Array.isArray(userRow?.constructionRoles) ? userRow.constructionRoles : [];
  if (construction.some((r) => COLLABORATOR_PERSONA_KEYS.has(safeStr(r).toLowerCase()))) return false;
  return true;
}

/** `users.role` still admin after project_admin demotion (missing marker or incomplete downgrade). */
export function hasStaleProjectAdminAuthRole(userRow) {
  return safeStr(userRow?.role).toLowerCase() === "admin" && !isDedicatedGlobalAdminUser(userRow);
}

/** Whether Save collaborators should clear admin-equivalent auth role. */
export function shouldApplyProjectAdminDowngrade(userData, row) {
  if (isDedicatedGlobalAdminUser(userData)) return false;
  if (isProjectAdminEquivalentUser(userData)) return true;
  if (!hasStaleProjectAdminAuthRole(userData)) return false;
  if (!row) return true;
  return !isProjectAdminEquivalentRole(row.role);
}

function isClientCollaboratorRole(roleValue) {
  return safeStr(roleValue).toLowerCase() === "client";
}

function stripProjectAdminEquivalentFromUserTypes(existingTypes, { keepClient = false } = {}) {
  const drop = new Set(["admin", "project_admin"]);
  if (!keepClient) drop.add("client");
  return uniqueStrings(
    (Array.isArray(existingTypes) ? existingTypes : []).filter(
      (t) => !drop.has(safeStr(t).toLowerCase())
    )
  );
}

/** `users.role` after removing temporary project_admin admin-equivalent access. */
export function portalRoleAfterProjectAdminDowngrade(collaboratorRole) {
  const role = safeStr(collaboratorRole).trim().toLowerCase();
  if (isClientCollaboratorRole(role)) return "client";
  return "construction_collaborator";
}

/** Eligible for collaborator User account dropdown and one-click link. */
export function isLinkablePortalUser(userRow) {
  if (!safeStr(userRow?.id || userRow?.uid || "").trim()) return false;
  if (!normalizeInviteEmail(userRow?.email || userRow?.emailLower)) return false;
  if (isArchivedPortalUser(userRow)) return false;
  if (isDedicatedGlobalAdminUser(userRow)) return false;
  return true;
}

/** Firestore `users.role` for invite signup (project_admin → admin per CL-338C). */
export function portalUserRoleFromInviteIntent(intent) {
  const projectRole = safeStr(intent?.primaryUserType || intent?.role).toLowerCase();
  if (isProjectAdminEquivalentRole(projectRole)) return "admin";
  if (projectRole === "client") return "client";
  return "construction_collaborator";
}

/** Merge allowedUserTypes when saving a collaborator row onto users/{uid}. */
export function mergeAllowedUserTypesForCollaborator(existingTypes, roleValue) {
  const role = safeStr(roleValue).trim().toLowerCase();
  const next = uniqueStrings(Array.isArray(existingTypes) ? existingTypes : []);
  if (!next.includes("client_portal")) next.push("client_portal");
  if (!next.includes("construction")) next.push("construction");
  if (isProjectAdminEquivalentRole(role)) {
    ["admin", "construction_collaborator", "project_admin"].forEach((t) => {
      if (!next.includes(t)) next.push(t);
    });
    return uniqueStrings(next);
  }
  if (role === "client") {
    if (!next.includes("client")) next.push("client");
  } else if (role) {
    if (!next.includes("construction_collaborator")) next.push("construction_collaborator");
    if (!next.includes(role)) next.push(role);
  }
  return uniqueStrings(next);
}

/**
 * Fields to merge onto users/{uid} when linking a collaborator with a portal account (CL-338C).
 */
export function buildCollaboratorLinkedUserPatch(row, userData = {}, projectId = "") {
  const role = safeStr(row?.role).trim().toLowerCase();
  const pid = safeStr(projectId);
  const patch = {
    primaryUserType: role,
    allowedUserTypes: mergeAllowedUserTypesForCollaborator(
      userData.allowedUserTypes || userData.userTypes || [],
      role
    ),
    constructionRoles: uniqueStrings([
      ...(Array.isArray(userData.constructionRoles) ? userData.constructionRoles : []),
      role,
    ]),
    assignedProjectIds: uniqueStrings([
      ...(Array.isArray(userData.assignedProjectIds) ? userData.assignedProjectIds : []),
      ...(pid ? [pid] : []),
    ]),
    accessScopes: uniqueStrings([
      ...(Array.isArray(userData.accessScopes) ? userData.accessScopes : []),
      safeStr(row?.accessScope),
    ]),
  };
  if (isProjectAdminEquivalentRole(role)) {
    patch.role = "admin";
    patch.primaryUserType = "project_admin";
    patch[PROJECT_ADMIN_EQUIVALENT_FIELD] = true;
  }
  return patch;
}

/**
 * Revert CL-338C admin-equivalent fields when project_admin is removed (CL-338E).
 * @param {object} row Collaborator row on this project (role, accessScope, …)
 * @param {object} userData Existing users/{uid} document
 */
export function buildProjectAdminDowngradeUserPatch(row, userData = {}) {
  const role = safeStr(row?.role).trim().toLowerCase() || "designer";
  const strippedTypes = stripProjectAdminEquivalentFromUserTypes(
    userData.allowedUserTypes || userData.userTypes || [],
    { keepClient: isClientCollaboratorRole(role) }
  );
  const constructionRoles = uniqueStrings([
    ...(Array.isArray(userData.constructionRoles) ? userData.constructionRoles : [])
      .map((r) => safeStr(r).toLowerCase())
      .filter((r) => r && r !== "project_admin" && (isClientCollaboratorRole(role) || r !== "client")),
    role,
  ]);
  const archived = isArchivedPortalUser(userData);
  return {
    role: portalRoleAfterProjectAdminDowngrade(role),
    primaryUserType: role,
    allowedUserTypes: mergeAllowedUserTypesForCollaborator(strippedTypes, role),
    constructionRoles,
    [PROJECT_ADMIN_EQUIVALENT_FIELD]: false,
    portalEnabled: true,
    status: archived ? "archived" : "active",
    services: uniqueStrings([...(Array.isArray(userData.services) ? userData.services : []), "construction"]),
  };
}

/** When the user is no longer on the project roster — infer role from profile. */
export function buildProjectAdminDowngradeUserPatchFromProfile(userData = {}) {
  const fromPrimary = safeStr(userData.primaryUserType).toLowerCase();
  const fromRoles = (Array.isArray(userData.constructionRoles) ? userData.constructionRoles : [])
    .map((r) => safeStr(r).toLowerCase())
    .find((r) => r && r !== "project_admin");
  const role =
    (fromPrimary && fromPrimary !== "project_admin" ? fromPrimary : "") ||
    fromRoles ||
    "designer";
  return buildProjectAdminDowngradeUserPatch({ role }, userData);
}

/**
 * Active project_admin on any projectAssignments row for this uid (post-save).
 * @param {import('firebase/firestore').Firestore} db
 */
export async function userHasActiveProjectAdminAssignment(db, uid) {
  const id = safeStr(uid).trim();
  if (!db || !id) return false;
  const snap = await getDocs(
    query(collection(db, "projectAssignments"), where("userId", "==", id), limit(200))
  );
  return snap.docs.some((d) => {
    const data = d.data() || {};
    return (
      isProjectAdminEquivalentRole(data.role) &&
      safeStr(data.status || "active").toLowerCase() === "active"
    );
  });
}

export function uniqueStrings(values) {
  return Array.from(new Set((Array.isArray(values) ? values : []).map((v) => safeStr(v)).filter(Boolean)));
}

export function randomInviteCode() {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

/** Default intent for legacy invites (no admin intent fields). */
export function defaultLegacyInviteIntent() {
  return {
    targetEmailLower: "",
    role: "client",
    primaryUserType: "client",
    services: ["construction"],
    clientType: "construction",
    projectIds: [],
    accessScope: "full_project",
  };
}

/**
 * Build Firestore payload for invites/{code} (merge with used flags on read paths only).
 * @param {object} params
 * @param {string|null} params.createdBy admin uid
 * @param {string} [params.targetEmailLower] normalized email — empty = any email at signup
 * @param {string} [params.role]
 * @param {string} [params.primaryUserType]
 * @param {string[]} [params.services]
 * @param {string} [params.clientType] construction | realEstate | both
 * @param {string[]} [params.projectIds] pmProjects ids
 * @param {string} [params.accessScope] e.g. shared_files | full_project
 */
export function buildInviteIntentPayload({
  createdBy = null,
  targetEmailLower = "",
  role = "client",
  primaryUserType = "client",
  services = ["construction"],
  clientType = "construction",
  projectIds = [],
  accessScope = "full_project",
} = {}) {
  const emailLower = normalizeInviteEmail(targetEmailLower);
  const svc = uniqueStrings(services.length ? services : ["construction"]);
  let ct = safeStr(clientType).toLowerCase();
  if (!ct || ct === "none") ct = "construction";
  if (svc.includes("realEstate") && svc.includes("construction")) ct = "both";
  else if (svc.includes("realEstate") && ct !== "both") ct = "realEstate";
  else if (svc.includes("construction") && ct !== "both") ct = "construction";

  const r = safeStr(role).toLowerCase() || "client";
  const pt = safeStr(primaryUserType).toLowerCase() || r;

  return {
    used: false,
    createdBy: createdBy || null,
    targetEmailLower: emailLower || null,
    role: r,
    primaryUserType: pt,
    services: svc,
    clientType: ct,
    projectIds: uniqueStrings(projectIds),
    accessScope: safeStr(accessScope).toLowerCase() || "full_project",
  };
}

/**
 * Normalize invite document data for signup (supports legacy docs).
 * @param {object} raw invite snapshot .data()
 */
export function deriveProfileFieldsFromInviteData(raw) {
  const data = raw && typeof raw === "object" ? raw : {};
  const legacy = defaultLegacyInviteIntent();

  const services = uniqueStrings(
    Array.isArray(data.services) && data.services.length ? data.services : legacy.services
  );
  const clientType = safeStr(data.clientType).toLowerCase() || legacy.clientType;
  const role = safeStr(data.role).toLowerCase() || legacy.role;
  const primaryUserType = safeStr(data.primaryUserType).toLowerCase() || role || legacy.primaryUserType;
  const projectIds = uniqueStrings(Array.isArray(data.projectIds) ? data.projectIds : []);
  const accessScope = safeStr(data.accessScope).toLowerCase() || legacy.accessScope;
  const targetEmailLower = data.targetEmailLower != null ? normalizeInviteEmail(data.targetEmailLower) : "";

  return {
    targetEmailLower,
    role,
    primaryUserType,
    services,
    clientType,
    projectIds,
    accessScope,
    accessScopes: accessScope ? [accessScope] : [],
  };
}

export function buildAllowedUserTypesForInviteRole(role, services) {
  const types = ["client_portal"];
  const roleNorm = safeStr(role).toLowerCase();
  const servList = uniqueStrings(services);
  if (servList.includes("construction")) types.push("construction");
  if (servList.includes("realEstate")) types.push("realEstate");
  if (isProjectAdminEquivalentRole(roleNorm)) {
    types.push("admin", "construction_collaborator", "project_admin");
  } else if (roleNorm === "client") {
    types.push("client");
  } else {
    types.push("construction_collaborator");
    if (roleNorm) types.push(roleNorm);
  }
  return uniqueStrings(types);
}

export function buildConstructionRolesFromInvite(role) {
  const roleNorm = safeStr(role).toLowerCase();
  if (!roleNorm || roleNorm === "client") return [];
  return uniqueStrings([roleNorm]);
}

/**
 * CL-337B: Invite intent from a PM collaborator row (must mirror row + linked contact, not generic defaults).
 * Uses `email` or `emailLower` on the row; when `row.uid` is set, pass `linkedUser` from the admin users picklist
 * (must include `services` / `clientType` from Firestore).
 *
 * @param {object} params
 * @param {object} params.row Collaborator row (role, accessScope, email, emailLower, uid, …)
 * @param {string} params.projectId Current pmProjects id
 * @param {string|null} [params.createdBy] Admin uid
 * @param {object|null} [params.linkedUser] Matching directory user when row.uid is set
 */
/** Email that will be locked on invites/{code}.targetEmailLower (saved collaborator row only). */
export function resolveCollaboratorInviteEmail(row, linkedUser = null) {
  return normalizeInviteEmail(row?.email || row?.emailLower || linkedUser?.email || "");
}

export function countUsersMatchingEmail(userRows, emailLower) {
  const em = normalizeInviteEmail(emailLower);
  if (!em) return 0;
  return (Array.isArray(userRows) ? userRows : []).filter(
    (u) => isLinkablePortalUser(u) && normalizeInviteEmail(u?.email || u?.emailLower) === em
  ).length;
}

/** Returns the sole matching user row, or null if zero or multiple matches. */
export function findSingleUserMatchingEmail(userRows, emailLower) {
  const em = normalizeInviteEmail(emailLower);
  if (!em) return null;
  const matches = (Array.isArray(userRows) ? userRows : []).filter(
    (u) => isLinkablePortalUser(u) && normalizeInviteEmail(u?.email || u?.emailLower) === em
  );
  if (matches.length !== 1) return null;
  return matches[0];
}

/**
 * Unused invites for the same project + normalized email (client-side filter over loaded rows).
 * @param {Array<{ id?: string, used?: boolean, targetEmailLower?: string|null, projectIds?: string[] }>} inviteRows
 */
export function findUnusedInvitesForProjectAndEmail(inviteRows, projectId, emailLower) {
  const pid = safeStr(projectId);
  const em = normalizeInviteEmail(emailLower);
  if (!pid || !em) return [];
  return (Array.isArray(inviteRows) ? inviteRows : []).filter((inv) => {
    if (inv?.used) return false;
    const target = inv.targetEmailLower != null ? normalizeInviteEmail(inv.targetEmailLower) : "";
    if (!target || target !== em) return false;
    const pids = uniqueStrings(Array.isArray(inv.projectIds) ? inv.projectIds : []);
    return pids.includes(pid);
  });
}

export function buildCollaboratorPortalInvitePayload({
  row,
  projectId,
  createdBy = null,
  linkedUser = null,
} = {}) {
  const emailLower = resolveCollaboratorInviteEmail(row, linkedUser);
  const role = safeStr(row?.role).trim().toLowerCase() || "designer";
  const accessScope = safeStr(row?.accessScope).trim().toLowerCase() || "full_project";

  let services = uniqueStrings(["construction"]);
  const u = linkedUser && typeof linkedUser === "object" ? linkedUser : null;
  if (u) {
    if (Array.isArray(u.services) && u.services.length) {
      services = uniqueStrings(u.services);
    } else {
      const ct0 = safeStr(u.clientType).toLowerCase();
      if (ct0 === "both") services = uniqueStrings(["realEstate", "construction"]);
      else if (ct0 === "realEstate") services = uniqueStrings(["realEstate"]);
      else services = uniqueStrings(["construction"]);
    }
  }

  let clientType = "construction";
  if (services.includes("realEstate") && services.includes("construction")) clientType = "both";
  else if (services.includes("realEstate")) clientType = "realEstate";
  else if (services.includes("construction")) clientType = "construction";

  const pid = safeStr(projectId);
  const projectIds = pid ? [pid] : [];

  return buildInviteIntentPayload({
    createdBy,
    targetEmailLower: emailLower,
    role,
    primaryUserType: role,
    services,
    clientType,
    projectIds,
    accessScope,
  });
}
